import { createReducer, on } from '@ngrx/store';
import * as TransactionActions from './transaction.actions';
import {
  TransactionFilters,
  TransactionItem,
  TransactionType,
  GetTransactionDetailResponse,
  TransactionProjectionStatusEnum,
  CreateWithdrawalResponse,
  isTerminalTransactionProjectionStatus,
  mapProjectionStatusToExecutionStep,
} from '../models/transaction.model';
import type { ApprovalProgressData } from '@/app/shared/models/approval.model';
import type {
  SigningNotificationChannelApi,
  TransactionNetworkDetailsData,
  TransactionBlockchainDetailsData,
  TransactionStep01DetailsData,
  TransactionDetailFailure,
} from '../models/transaction.model';
import type {
  NetworkFeeData,
  WithdrawalLimitSummaryData,
  FeeOptionsData,
  FeeChoice,
} from '../models/transaction.model';

export const TRANSACTION_FEATURE_KEY = 'transaction';

export interface TransactionState {
  transactions: TransactionItem[];
  listLoading: boolean;
  listError: string | null;
  total: number;
  pageIndex: number;
  pageSize: number;
  activeTab: TransactionType;
  sortActive: string;
  sortDirection: 'ASC' | 'DESC';
  filtersByTab: Record<TransactionType, TransactionFilters>;
  searchTerm: string;
  filterModalOpen: boolean;

  detailTransactionId: string | null;
  detail: GetTransactionDetailResponse | null;
  detailLoading: boolean;
  detailError: string | null;
  detailSelectedStep: number;

  approvalProgress: ApprovalProgressData | null;
  approvalProgressLoading: boolean;
  approvalProgressError: string | null;

  transactionStep01Details: TransactionStep01DetailsData | null;
  transactionStep01DetailsLoading: boolean;
  transactionStep01DetailsError: string | null;

  signingLoading: boolean;
  signingError: string | null;
  signingStepStatus: TransactionProjectionStatusEnum | null;
  signingUpdatedAt: string;
  signingChannels: SigningNotificationChannelApi[];

  networkLoading: boolean;
  networkError: string | null;
  networkDetails: TransactionNetworkDetailsData | null;

  blockchainDetailsLoading: boolean;
  blockchainDetailsError: string | null;
  blockchainDetails: TransactionBlockchainDetailsData | null;

  /**
   * POST broadcast (step 4) in flight — covers both the initial
   * verification-gated call AND the confirmation call after the shared
   * OTP step succeeds. The actionId / method / expiresAt / verify-code
   * UX live in the shared `verification` slice (see
   * `@shared/store/verification`).
   */
  broadcastSubmitting: boolean;

  /**
   * Step 3 "Choose fee & Execute": tiered fee options (GET /fee-options), the
   * POST /execute in-flight flag (covers initial + verified confirm call), and the
   * chosen `feeChoice` retained across the OTP step so the verified confirm call
   * can resend it (the challenge is fingerprinted on it). Verification UX lives in
   * the shared `verification` slice.
   */
  feeOptions: FeeOptionsData | null;
  feeOptionsLoading: boolean;
  feeOptionsError: string | null;
  executeSubmitting: boolean;
  pendingExecuteFeeChoice: FeeChoice | null;
  cancelSubmitting: boolean;
  /** Reason sent with the first cancel call, replayed on the verified retry. */
  pendingCancelReason: string | null;

  createWithdrawalSubmitting: boolean;
  createWithdrawalError: string | null;
  createWithdrawalResponse: CreateWithdrawalResponse | null;
  createWithdrawalStatusCode: number | null;

  createWithdrawalPrefill: {
    vaultId: string;
    walletId: string;
    whitelistAddressId?: string | null;
  } | null;

  /** Create withdrawal — network fee hint (GET /network/:id/fee) */
  createFlowNetworkFee: NetworkFeeData | null;
  createFlowNetworkFeeLoading: boolean;
  createFlowNetworkFeeError: string | null;

  createFlowWithdrawalLimitSummary: WithdrawalLimitSummaryData | null;
  /** Detail page step 1's limit summary, for one wallet (F-072). */
  detailLimitSummary: {
    walletId: string;
    data: WithdrawalLimitSummaryData | null;
    loading: boolean;
    error: string | null;
  } | null;
  createFlowWithdrawalLimitSummaryLoading: boolean;
  createFlowWithdrawalLimitSummaryError: string | null;

  /** Create withdrawal — asset->USDT pricing rate for equivalents */
  createFlowPricingRateByBaseSymbol: Record<string, number>;
  createFlowPricingRateLoadingByBaseSymbol: Record<string, boolean>;
  createFlowPricingRateErrorByBaseSymbol: Record<string, string | null>;
}

export const initialState: TransactionState = {
  transactions: [],
  listLoading: false,
  listError: null,
  total: 0,
  pageIndex: 0,
  pageSize: 10,
  activeTab: 'ALL',
  sortActive: '',
  sortDirection: 'DESC',
  filtersByTab: {
    ALL: {},
    DEPOSIT: {},
    WITHDRAWAL: {},
  },
  searchTerm: '',
  filterModalOpen: false,

  detailTransactionId: null,
  detail: null,
  detailLoading: false,
  detailError: null,
  detailSelectedStep: 1,

  approvalProgress: null,
  approvalProgressLoading: false,
  approvalProgressError: null,

  transactionStep01Details: null,
  transactionStep01DetailsLoading: false,
  transactionStep01DetailsError: null,

  signingLoading: false,
  signingError: null,
  signingStepStatus: null,
  signingUpdatedAt: '',
  signingChannels: [],

  networkLoading: false,
  networkError: null,
  networkDetails: null,

  blockchainDetailsLoading: false,
  blockchainDetailsError: null,
  blockchainDetails: null,

  broadcastSubmitting: false,

  feeOptions: null,
  feeOptionsLoading: false,
  feeOptionsError: null,
  executeSubmitting: false,
  pendingExecuteFeeChoice: null,
  cancelSubmitting: false,
  pendingCancelReason: null,

  createWithdrawalSubmitting: false,
  createWithdrawalError: null,
  createWithdrawalResponse: null,
  createWithdrawalStatusCode: null,

  createWithdrawalPrefill: null,

  createFlowNetworkFee: null,
  createFlowNetworkFeeLoading: false,
  createFlowNetworkFeeError: null,

  createFlowWithdrawalLimitSummary: null,
  detailLimitSummary: null,
  createFlowWithdrawalLimitSummaryLoading: false,
  createFlowWithdrawalLimitSummaryError: null,

  createFlowPricingRateByBaseSymbol: {},
  createFlowPricingRateLoadingByBaseSymbol: {},
  createFlowPricingRateErrorByBaseSymbol: {},
};

/**
 * A failed broadcast / execute call puts the step it failed at back on the
 * open detail — but only the detail the call was for, and never over a state
 * the websocket has already moved past: a reply lost on the wire must not
 * repaint a FINALIZED withdrawal as in progress (F-060). Stricter than
 * `applyDetailStatusFromEvent`'s step check: BROADCASTING and CONFIRMING share
 * a step, and a CONFIRMING withdrawal must not be stamped back.
 */
/** The non-terminal statuses in the order a withdrawal moves through them. */
const IN_FLIGHT_ORDER: Partial<Record<string, number>> = {
  [TransactionProjectionStatusEnum.PENDING]: 0,
  [TransactionProjectionStatusEnum.AWAITING_EXECUTION]: 1,
  [TransactionProjectionStatusEnum.SIGNING]: 2,
  [TransactionProjectionStatusEnum.BROADCASTING]: 3,
  [TransactionProjectionStatusEnum.CONFIRMING]: 4,
};

function stampCallFailure(
  detail: TransactionState['detail'],
  transactionId: string,
  status: TransactionProjectionStatusEnum,
  failure: TransactionDetailFailure
): TransactionState['detail'] {
  if (!detail || detail.data.id !== transactionId) {
    return detail;
  }
  const current = detail.data.status.state;
  if (
    isTerminalTransactionProjectionStatus(current) ||
    (IN_FLIGHT_ORDER[current] ?? 0) > (IN_FLIGHT_ORDER[status] ?? 0)
  ) {
    return detail;
  }
  return {
    ...detail,
    data: {
      ...detail.data,
      status: { ...detail.data.status, state: status, failure },
    },
  };
}

export const transactionReducer = createReducer(
  initialState,

  on(TransactionActions.loadTransactions, state => ({
    ...state,
    listLoading: true,
    listError: null,
  })),

  on(TransactionActions.loadTransactionsSuccess, (state, { data, total }) => ({
    ...state,
    transactions: data,
    total,
    listLoading: false,
    listError: null,
  })),

  on(TransactionActions.loadTransactionsFailure, (state, { error }) => ({
    ...state,
    listLoading: false,
    listError: error,
    transactions: [],
    total: 0,
  })),

  on(TransactionActions.setActiveTab, (state, { tab }) => ({
    ...state,
    activeTab: tab,
    pageIndex: 0,
  })),

  on(TransactionActions.setPagination, (state, { pageIndex, pageSize }) => ({
    ...state,
    pageIndex,
    pageSize,
  })),

  on(TransactionActions.setSort, (state, { active, direction }) => ({
    ...state,
    sortActive: active,
    sortDirection: direction,
    pageIndex: 0,
  })),

  on(TransactionActions.setFilters, (state, { filters }) => ({
    ...state,
    filtersByTab: {
      ...state.filtersByTab,
      [state.activeTab]: filters,
    },
    pageIndex: 0,
    filterModalOpen: false,
  })),

  on(TransactionActions.clearFilters, state => ({
    ...state,
    filtersByTab: {
      ...state.filtersByTab,
      [state.activeTab]: {},
    },
    pageIndex: 0,
    filterModalOpen: false,
  })),

  on(TransactionActions.removeFilterChip, (state, { key, value }) => {
    const currentFilters = { ...state.filtersByTab[state.activeTab] };

    switch (key) {
      case 'dateRange':
        currentFilters.fromDate = null;
        currentFilters.toDate = null;
        break;
      case 'vault':
        currentFilters.relatedVaultId = null;
        break;
      case 'wallet':
        currentFilters.relatedWalletId = null;
        break;
      case 'requester':
        currentFilters.requesterIds = (
          currentFilters.requesterIds ?? []
        ).filter(id => id !== value);
        if (!currentFilters.requesterIds?.length) {
          currentFilters.requesterIds = null;
        }
        break;
      case 'status':
        currentFilters.status = null;
        break;
    }

    return {
      ...state,
      filtersByTab: {
        ...state.filtersByTab,
        [state.activeTab]: currentFilters,
      },
      pageIndex: 0,
    };
  }),

  on(TransactionActions.setSearch, (state, { searchTerm }) => ({
    ...state,
    searchTerm,
    pageIndex: 0,
  })),

  on(TransactionActions.openFilterModal, state => ({
    ...state,
    filterModalOpen: true,
  })),

  on(TransactionActions.closeFilterModal, state => ({
    ...state,
    filterModalOpen: false,
  })),

  on(TransactionActions.loadTransactionDetail, (state, { transactionId }) => ({
    ...state,
    detailTransactionId: transactionId,
    detail: null,
    detailLoading: true,
    detailError: null,
    detailSelectedStep: 1,
    approvalProgress: null,
    approvalProgressLoading: false,
    approvalProgressError: null,
    transactionStep01Details: null,
    transactionStep01DetailsLoading: false,
    transactionStep01DetailsError: null,
    signingStepStatus: null,
    signingUpdatedAt: '',
    signingChannels: [],
    signingError: null,
    networkDetails: null,
    networkLoading: false,
    networkError: null,
    blockchainDetails: null,
    blockchainDetailsLoading: false,
    blockchainDetailsError: null,
    broadcastSubmitting: false,
  })),

  on(
    TransactionActions.loadTransactionDetailSuccess,
    (state, { response }) => ({
      ...state,
      detail: response,
      detailLoading: false,
      detailError: null,
    })
  ),

  on(
    TransactionActions.applyDetailStatusFromEvent,
    (state, { walletId, projectionState, failure }) => {
      // Gate on the open detail's wallet — the websocket events carry walletId,
      // not the projection id (which is distinct from the on-chain id).
      if (!state.detail || state.detail.data.wallet.id !== walletId) {
        return state;
      }
      const currentState = state.detail.data.status.state;
      // Terminal states (FINALIZED / FAILED / CANCELLED) may apply from any step,
      // but never regress a terminal state back to a non-terminal one, and never
      // move the stepper backward on a non-terminal update — both guard against
      // duplicate / out-of-order websocket delivery.
      if (!isTerminalTransactionProjectionStatus(projectionState)) {
        if (isTerminalTransactionProjectionStatus(currentState)) {
          return state;
        }
        if (
          mapProjectionStatusToExecutionStep(projectionState) <
          mapProjectionStatusToExecutionStep(currentState)
        ) {
          return state;
        }
      }
      return {
        ...state,
        detail: {
          ...state.detail,
          data: {
            ...state.detail.data,
            status: {
              ...state.detail.data.status,
              state: projectionState,
              ...(failure !== undefined ? { failure } : {}),
            },
          },
        },
        // Keep the network / blockchain slice statuses in lockstep, mirroring
        // what a full refetch of all three endpoints would have done.
        ...(state.networkDetails
          ? {
              networkDetails: {
                ...state.networkDetails,
                status: projectionState,
              },
            }
          : {}),
        ...(state.blockchainDetails
          ? {
              blockchainDetails: {
                ...state.blockchainDetails,
                status: projectionState,
              },
            }
          : {}),
      };
    }
  ),

  on(TransactionActions.loadTransactionDetailFailure, (state, { error }) => ({
    ...state,
    detail: null,
    detailLoading: false,
    detailError: error,
    approvalProgress: null,
    approvalProgressLoading: false,
    approvalProgressError: null,
    blockchainDetails: null,
    blockchainDetailsLoading: false,
    blockchainDetailsError: null,
  })),

  on(TransactionActions.loadApprovalProgressByExternalId, state => ({
    ...state,
    approvalProgress: null,
    approvalProgressLoading: true,
    approvalProgressError: null,
  })),

  on(
    TransactionActions.loadApprovalProgressByExternalIdSuccess,
    (state, { data }) => ({
      ...state,
      approvalProgress: data,
      approvalProgressLoading: false,
      approvalProgressError: null,
    })
  ),

  on(
    TransactionActions.loadApprovalProgressByExternalIdFailure,
    (state, { error }) => ({
      ...state,
      approvalProgress: null,
      approvalProgressLoading: false,
      approvalProgressError: error,
    })
  ),

  on(
    TransactionActions.refreshApprovalProgressByExternalIdSuccess,
    (state, { data }) => ({
      ...state,
      approvalProgress: data,
      approvalProgressError: null,
    })
  ),

  on(TransactionActions.loadTransactionStep01Details, state => ({
    ...state,
    transactionStep01Details: null,
    transactionStep01DetailsLoading: true,
    transactionStep01DetailsError: null,
  })),

  on(
    TransactionActions.loadTransactionStep01DetailsSuccess,
    (state, { data }) => ({
      ...state,
      transactionStep01Details: data,
      transactionStep01DetailsLoading: false,
      transactionStep01DetailsError: null,
    })
  ),

  on(
    TransactionActions.loadTransactionStep01DetailsFailure,
    (state, { error }) => ({
      ...state,
      transactionStep01Details: null,
      transactionStep01DetailsLoading: false,
      transactionStep01DetailsError: error,
    })
  ),

  on(TransactionActions.clearTransactionDetail, state => ({
    ...state,
    detailTransactionId: null,
    detail: null,
    detailLoading: false,
    detailError: null,
    detailSelectedStep: 1,
    approvalProgress: null,
    approvalProgressLoading: false,
    approvalProgressError: null,
    transactionStep01Details: null,
    transactionStep01DetailsLoading: false,
    transactionStep01DetailsError: null,
    signingLoading: false,
    signingError: null,
    signingStepStatus: null,
    signingUpdatedAt: '',
    signingChannels: [],
    networkLoading: false,
    networkError: null,
    networkDetails: null,
    blockchainDetails: null,
    blockchainDetailsLoading: false,
    blockchainDetailsError: null,
    broadcastSubmitting: false,
  })),

  on(TransactionActions.setDetailSelectedStep, (state, { step }) => ({
    ...state,
    detailSelectedStep: step,
  })),

  on(TransactionActions.loadSigningChannels, state => ({
    ...state,
    signingLoading: true,
    signingError: null,
  })),

  on(TransactionActions.loadSigningChannelsSuccess, (state, { response }) => ({
    ...state,
    signingLoading: false,
    signingError: null,
    signingStepStatus: response.data.status,
    signingUpdatedAt: response.data.updatedAt,
    signingChannels: response.data.notificationChannels ?? [],
  })),

  on(TransactionActions.loadSigningChannelsFailure, (state, { error }) => ({
    ...state,
    signingLoading: false,
    signingError: error,
  })),

  on(
    TransactionActions.applySigningStatusFromEvent,
    (state, { walletId, projectionState, updatedAt }) => {
      if (!state.detail || state.detail.data.wallet.id !== walletId) {
        return state;
      }
      return {
        ...state,
        signingError: null,
        signingStepStatus: projectionState,
        signingUpdatedAt: updatedAt,
      };
    }
  ),

  on(TransactionActions.loadNetworkDetails, state => ({
    ...state,
    networkLoading: true,
    networkError: null,
  })),

  on(TransactionActions.loadNetworkDetailsSuccess, (state, { response }) => ({
    ...state,
    networkLoading: false,
    networkError: null,
    networkDetails: response.data,
  })),

  on(TransactionActions.loadNetworkDetailsFailure, (state, { error }) => ({
    ...state,
    networkLoading: false,
    networkError: error,
  })),

  on(
    TransactionActions.applyNetworkDetailsFromEvent,
    (
      state,
      { walletId, transactionHash, explorerUrl, projectionState, updatedAt }
    ) => {
      // Patch only — the assetNetwork block comes from the initial fetch, which
      // the websocket payload doesn't carry. No baseline yet → skip (the step-4
      // initial load will populate the hash from the API on entry).
      if (
        !state.detail ||
        state.detail.data.wallet.id !== walletId ||
        !state.networkDetails
      ) {
        return state;
      }
      return {
        ...state,
        networkError: null,
        networkDetails: {
          ...state.networkDetails,
          transactionHash,
          explorerUrl,
          status: projectionState,
          updatedAt,
        },
      };
    }
  ),

  on(TransactionActions.loadBlockchainDetails, state => ({
    ...state,
    blockchainDetailsLoading: true,
    blockchainDetailsError: null,
    blockchainDetails: null,
  })),

  on(
    TransactionActions.loadBlockchainDetailsSuccess,
    (state, { response }) => ({
      ...state,
      blockchainDetailsLoading: false,
      blockchainDetailsError: null,
      blockchainDetails: response.data,
    })
  ),

  on(TransactionActions.loadBlockchainDetailsFailure, (state, { error }) => ({
    ...state,
    blockchainDetailsLoading: false,
    blockchainDetailsError: error,
    blockchainDetails: null,
  })),

  on(
    TransactionActions.applyBlockchainDetailsFromEvent,
    (state, { walletId, confirmCount, blockNo, updatedAt }) => {
      // Patch the dynamic confirmation fields only; the static confirmationMode /
      // acceptableConfirmCount denominator stays from the initial fetch.
      if (
        !state.detail ||
        state.detail.data.wallet.id !== walletId ||
        !state.blockchainDetails
      ) {
        return state;
      }
      return {
        ...state,
        blockchainDetailsError: null,
        blockchainDetails: {
          ...state.blockchainDetails,
          confirmCount,
          ...(blockNo != null ? { blockNo } : {}),
          updatedAt,
        },
      };
    }
  ),

  on(TransactionActions.broadcastTransaction, state => ({
    ...state,
    broadcastSubmitting: true,
  })),

  on(TransactionActions.broadcastTransactionVerificationRequired, state => ({
    ...state,
    broadcastSubmitting: false,
  })),

  on(TransactionActions.confirmBroadcast, state => ({
    ...state,
    broadcastSubmitting: true,
  })),

  on(TransactionActions.broadcastTransactionSuccess, (state, { response }) => ({
    ...state,
    broadcastSubmitting: false,
    detail: response,
  })),

  on(
    TransactionActions.broadcastTransactionFailure,
    (state, { transactionId, failure }) => ({
      ...state,
      broadcastSubmitting: false,
      detail: stampCallFailure(
        state.detail,
        transactionId,
        TransactionProjectionStatusEnum.BROADCASTING,
        failure
      ),
    })
  ),

  on(TransactionActions.loadFeeOptions, state => ({
    ...state,
    feeOptionsLoading: true,
    feeOptionsError: null,
  })),

  on(TransactionActions.loadFeeOptionsSuccess, (state, { data }) => ({
    ...state,
    feeOptionsLoading: false,
    feeOptionsError: null,
    feeOptions: data,
  })),

  on(TransactionActions.loadFeeOptionsFailure, (state, { error }) => ({
    ...state,
    feeOptionsLoading: false,
    feeOptionsError: error,
  })),

  on(TransactionActions.executeTransaction, (state, { feeChoice }) => ({
    ...state,
    executeSubmitting: true,
    pendingExecuteFeeChoice: feeChoice,
  })),

  on(TransactionActions.executeTransactionVerificationRequired, state => ({
    ...state,
    executeSubmitting: false,
  })),

  on(TransactionActions.confirmExecute, state => ({
    ...state,
    executeSubmitting: true,
  })),

  on(TransactionActions.executeTransactionSuccess, (state, { response }) => ({
    ...state,
    executeSubmitting: false,
    pendingExecuteFeeChoice: null,
    detail: response,
  })),

  on(
    TransactionActions.executeTransactionFailure,
    (state, { transactionId, failure }) => ({
      ...state,
      executeSubmitting: false,
      pendingExecuteFeeChoice: null,
      detail: stampCallFailure(
        state.detail,
        transactionId,
        TransactionProjectionStatusEnum.AWAITING_EXECUTION,
        failure
      ),
    })
  ),

  on(TransactionActions.cancelTransaction, (state, { reason }) => ({
    ...state,
    cancelSubmitting: true,
    pendingCancelReason: reason,
  })),

  on(TransactionActions.cancelTransactionVerificationRequired, state => ({
    ...state,
    cancelSubmitting: false,
  })),

  on(TransactionActions.confirmCancel, state => ({
    ...state,
    cancelSubmitting: true,
  })),

  on(TransactionActions.cancelTransactionSuccess, (state, { response }) => ({
    ...state,
    cancelSubmitting: false,
    pendingCancelReason: null,
    detail: response,
  })),

  on(TransactionActions.cancelTransactionFailure, state => ({
    ...state,
    cancelSubmitting: false,
    pendingCancelReason: null,
  })),

  on(TransactionActions.clearFeeOptions, state => ({
    ...state,
    feeOptions: null,
    feeOptionsLoading: false,
    feeOptionsError: null,
    pendingExecuteFeeChoice: null,
  })),

  on(TransactionActions.createWithdrawal, state => ({
    ...state,
    createWithdrawalSubmitting: true,
    createWithdrawalError: null,
    createWithdrawalResponse: null,
    createWithdrawalStatusCode: null,
  })),

  on(
    TransactionActions.createWithdrawalSuccess,
    (state, { response, status }) => ({
      ...state,
      createWithdrawalSubmitting: false,
      createWithdrawalError: null,
      createWithdrawalResponse: response,
      createWithdrawalStatusCode: status,
    })
  ),

  on(TransactionActions.createWithdrawalFailure, (state, { error }) => ({
    ...state,
    createWithdrawalSubmitting: false,
    createWithdrawalError: error,
  })),

  on(TransactionActions.setCreateWithdrawalPrefill, (state, { prefill }) => ({
    ...state,
    createWithdrawalPrefill: prefill,
  })),

  on(TransactionActions.clearCreateWithdrawalPrefill, state => ({
    ...state,
    createWithdrawalPrefill: null,
  })),

  on(TransactionActions.loadCreateFlowNetworkFee, state => ({
    ...state,
    createFlowNetworkFeeLoading: true,
    createFlowNetworkFeeError: null,
  })),

  on(TransactionActions.loadCreateFlowNetworkFeeSuccess, (state, { data }) => ({
    ...state,
    createFlowNetworkFeeLoading: false,
    createFlowNetworkFeeError: null,
    createFlowNetworkFee: data,
  })),

  on(
    TransactionActions.loadCreateFlowNetworkFeeFailure,
    (state, { error }) => ({
      ...state,
      createFlowNetworkFeeLoading: false,
      createFlowNetworkFeeError: error,
      createFlowNetworkFee: null,
    })
  ),

  on(
    TransactionActions.loadDetailWithdrawalLimitSummary,
    (state, { walletId }) => ({
      ...state,
      detailLimitSummary: { walletId, data: null, loading: true, error: null },
    })
  ),

  on(
    TransactionActions.loadDetailWithdrawalLimitSummarySuccess,
    (state, { walletId, data }) =>
      state.detailLimitSummary?.walletId === walletId
        ? {
            ...state,
            detailLimitSummary: { walletId, data, loading: false, error: null },
          }
        : state
  ),

  on(
    TransactionActions.loadDetailWithdrawalLimitSummaryFailure,
    (state, { walletId, error }) =>
      state.detailLimitSummary?.walletId === walletId
        ? {
            ...state,
            detailLimitSummary: {
              walletId,
              data: null,
              loading: false,
              error,
            },
          }
        : state
  ),

  on(TransactionActions.clearCreateFlowNetworkFee, state => ({
    ...state,
    createFlowNetworkFee: null,
    createFlowNetworkFeeLoading: false,
    createFlowNetworkFeeError: null,
    createFlowWithdrawalLimitSummary: null,
    createFlowWithdrawalLimitSummaryLoading: false,
    createFlowWithdrawalLimitSummaryError: null,
  })),

  on(TransactionActions.loadCreateFlowWithdrawalLimitSummary, state => ({
    ...state,
    createFlowWithdrawalLimitSummary: null,
    createFlowWithdrawalLimitSummaryLoading: true,
    createFlowWithdrawalLimitSummaryError: null,
  })),

  on(
    TransactionActions.loadCreateFlowWithdrawalLimitSummarySuccess,
    (state, { data }) => ({
      ...state,
      createFlowWithdrawalLimitSummary: data,
      createFlowWithdrawalLimitSummaryLoading: false,
      createFlowWithdrawalLimitSummaryError: null,
    })
  ),

  on(
    TransactionActions.loadCreateFlowWithdrawalLimitSummaryFailure,
    (state, { error }) => ({
      ...state,
      createFlowWithdrawalLimitSummary: null,
      createFlowWithdrawalLimitSummaryLoading: false,
      createFlowWithdrawalLimitSummaryError: error,
    })
  ),

  on(TransactionActions.loadCreateFlowPricingRate, (state, { baseSymbol }) => {
    const key = String(baseSymbol ?? '').toUpperCase();
    return {
      ...state,
      createFlowPricingRateLoadingByBaseSymbol: {
        ...state.createFlowPricingRateLoadingByBaseSymbol,
        [key]: true,
      },
      createFlowPricingRateErrorByBaseSymbol: {
        ...state.createFlowPricingRateErrorByBaseSymbol,
        [key]: null,
      },
    };
  }),

  on(
    TransactionActions.loadCreateFlowPricingRateSuccess,
    (state, { baseSymbol, rate }) => {
      const key = String(baseSymbol ?? '').toUpperCase();
      return {
        ...state,
        createFlowPricingRateByBaseSymbol: {
          ...state.createFlowPricingRateByBaseSymbol,
          [key]: rate,
        },
        createFlowPricingRateLoadingByBaseSymbol: {
          ...state.createFlowPricingRateLoadingByBaseSymbol,
          [key]: false,
        },
        createFlowPricingRateErrorByBaseSymbol: {
          ...state.createFlowPricingRateErrorByBaseSymbol,
          [key]: null,
        },
      };
    }
  ),

  on(
    TransactionActions.loadCreateFlowPricingRateFailure,
    (state, { baseSymbol, error }) => {
      const key = String(baseSymbol ?? '').toUpperCase();
      return {
        ...state,
        createFlowPricingRateLoadingByBaseSymbol: {
          ...state.createFlowPricingRateLoadingByBaseSymbol,
          [key]: false,
        },
        createFlowPricingRateErrorByBaseSymbol: {
          ...state.createFlowPricingRateErrorByBaseSymbol,
          [key]: error,
        },
      };
    }
  ),

  on(TransactionActions.clearCreateFlowPricingRates, state => ({
    ...state,
    createFlowPricingRateByBaseSymbol: {},
    createFlowPricingRateLoadingByBaseSymbol: {},
    createFlowPricingRateErrorByBaseSymbol: {},
  })),

  on(TransactionActions.resetState, () => initialState)
);
